import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type UserRef } from '../lib/api';
import { enumLabel, useI18n, type Locale } from '../lib/i18n';
import { refusalText } from '../lib/reach';
import { formatDate, isoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Checkbox, Field, Input, Select, useToast } from '../components/ui';
import { supportRefusal } from './SupportMode';

const BACK = '/shifts';
const DEFINITIONS_BACK = '/shifts?tab=definitions';
const HOLIDAYS_BACK = '/shifts?tab=holidays';

/**
 * §15.5 — one person's one day. Route:
 * `/shifts/day?userId=…&userName=…&date=…`, which keeps the roster grid's cell
 * click a plain link rather than lifted state.
 *
 * The day as it stands is read first: its shift, if it has one, and its support
 * hours (2026-11-01: "i will chose days and hours and i can also chose when click
 * on day in shifts"). Only what is changed is saved — a day with no shift may be
 * given support hours alone. `back=support` returns to Shifts › Support mode.
 */
export function ShiftDayFormPage() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') ?? '';
  const userName = searchParams.get('userName') ?? '';
  const date = searchParams.get('date') ?? isoDate();
  const back = searchParams.get('back') === 'support' ? '/shifts?tab=support' : BACK;

  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // '' is no shift: only a day without one starts there, and it is never written.
  const [type, setType] = useState('');
  const [shiftDefinitionId, setShiftDefinitionId] = useState('');
  const [shiftTouched, setShiftTouched] = useState(false);
  const [support, setSupport] = useState({ on: false, startTime: '18:00', endTime: '23:00' });
  const [loaded, setLoaded] = useState(false);

  const { data: definitions } = useQuery({
    queryKey: ['shift-definitions'],
    queryFn: () => api.get<any[]>('/shift-definitions'),
  });
  const existingShift = useQuery({
    queryKey: ['roster', 'day', userId, date],
    queryFn: () => api.get<any[]>('/shift-schedules', { userId, from: date, to: date }),
    enabled: !!userId,
  });
  const existingSupport = useQuery({
    queryKey: ['support-mode', 'days', 'day', userId, date],
    queryFn: () => api.get<{ items: SupportDayRef[] }>('/support-mode/days', { userId, from: date, to: date }),
    enabled: !!userId,
  });
  // Support mode is for people who handle tickets: the picker's own list says who.
  const supportPeople = useQuery({
    queryKey: ['support-mode', 'people'],
    queryFn: () => api.get<Array<{ id: string }>>('/support-mode/people'),
  });

  const shiftDay = existingShift.data?.[0] ?? null;
  const supportDay = existingSupport.data?.items[0] ?? null;
  if (!loaded && existingShift.data && existingSupport.data) {
    setLoaded(true);
    if (shiftDay) {
      setType(shiftDay.type);
      setShiftDefinitionId(shiftDay.shiftDefinitionId ?? '');
    }
    if (supportDay) setSupport({ on: true, startTime: supportDay.startTime, endTime: supportDay.endTime });
  }
  const canSupport = !!supportDay || !!supportPeople.data?.some((p) => p.id === userId);

  const writeShift = shiftTouched && type !== '';
  const supportChanged = support.on
    ? !supportDay || supportDay.startTime !== support.startTime || supportDay.endTime !== support.endTime
    : !!supportDay;

  const save = useMutation({
    mutationFn: async () => {
      if (writeShift) {
        await api.post('/shift-schedules', {
          userId,
          date,
          type,
          shiftDefinitionId: type === 'OFF' ? undefined : shiftDefinitionId || undefined,
        });
      }
      if (supportChanged) {
        if (support.on) {
          await api.put('/support-mode/days', { userId, date, startTime: support.startTime, endTime: support.endTime });
        } else if (supportDay) {
          await api.del(`/support-mode/days/${supportDay.id}`);
        }
      }
    },
    onSuccess: () => {
      toast.success(t('shifts.daySaved'));
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      void queryClient.invalidateQueries({ queryKey: ['support-mode'] });
      navigate(back);
    },
    onError: (e: any) => toast.error(supportRefusal(e, t)),
  });

  // A day keeps a shift no longer offered — switched off or removed — and shows it as its own.
  const offered = definitions ?? [];
  const own = shiftDay?.shiftDefinition;
  const matching = [...offered, ...(own && !offered.some((d) => d.id === own.id) ? [own] : [])].filter(
    (d) => d.type === type,
  );

  return (
    <FormPage
      // Each part isolated: an English name beside an Arabic date otherwise reads scrambled.
      title={
        <span>
          <bdi>{userName}</bdi> · <bdi>{formatDate(date, locale)}</bdi>
        </span>
      }
      backTo={back}
      backLabel={t('nav.shifts')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      loading={existingShift.isLoading || existingSupport.isLoading}
      error={existingShift.error ?? existingSupport.error}
      onRetry={() => {
        void existingShift.refetch();
        void existingSupport.refetch();
      }}
      disabled={
        (!writeShift && !supportChanged) ||
        (writeShift && type !== 'OFF' && !shiftDefinitionId) ||
        (support.on && (!support.startTime || !support.endTime))
      }
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('shifts.dayShift')}>
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setShiftDefinitionId('');
              setShiftTouched(true);
            }}
            autoFocus
          >
            {/* A day that has a shift keeps one: the roster has no delete. */}
            {!shiftDay && <option value="">{t('shifts.noShift')}</option>}
            {['MORNING', 'NIGHT', 'CUSTOM', 'OFF'].map((x) => (
              <option key={x} value={x}>{enumLabel(x, locale)}</option>
            ))}
          </Select>
        </Field>
        {type !== '' && type !== 'OFF' && (
          <Field label={t('shifts.definitions')} required={writeShift}>
            <Select
              value={shiftDefinitionId}
              onChange={(e) => {
                setShiftDefinitionId(e.target.value);
                setShiftTouched(true);
              }}
            >
              <option value="">—</option>
              {matching.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({shiftHoursLabel(d, t, locale)})
                </option>
              ))}
            </Select>
          </Field>
        )}
        <p className="text-caption text-faint">{t('shifts.snapshotNote')}</p>

        {/* Support mode (2026-11-01): this day's support hours, shift or no shift. */}
        <div className="space-y-3 border-t hairline pt-4">
          <p className="text-overline uppercase text-[var(--text-tertiary)]">{t('shifts.support')}</p>
          {canSupport ? (
            <>
              <Checkbox
                checked={support.on}
                onChange={(e) => setSupport({ ...support, on: e.target.checked })}
                label={t('shifts.daySupport')}
              />
              {support.on && (
                <>
                  <FormGrid>
                    <Field label={t('shifts.supportFrom')} required>
                      <Input
                        type="time"
                        value={support.startTime}
                        onChange={(e) => setSupport({ ...support, startTime: e.target.value })}
                      />
                    </Field>
                    <Field label={t('shifts.supportTo')} required hint={t('shifts.supportHoursHint')}>
                      <Input
                        type="time"
                        value={support.endTime}
                        onChange={(e) => setSupport({ ...support, endTime: e.target.value })}
                      />
                    </Field>
                  </FormGrid>
                  <p className="text-caption text-muted">{t('shifts.daySupportHint')}</p>
                </>
              )}
            </>
          ) : (
            <p className="text-caption text-muted">{supportPeople.isLoading ? '…' : t('shifts.supportNotForThem')}</p>
          )}
        </div>
      </div>
    </FormPage>
  );
}

/** A day's support hours, as `GET /support-mode/days` lists them. */
interface SupportDayRef {
  id: string;
  startTime: string;
  endTime: string;
}

/** Route: `/shifts/generate?year=…&month=…`. */
export function GenerateRosterFormPage() {
  const [searchParams] = useSearchParams();
  const now = new Date();
  const year = Number(searchParams.get('year') ?? now.getUTCFullYear());
  const month = Number(searchParams.get('month') ?? now.getUTCMonth() + 1);

  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const [form, setForm] = useState({
    userIds: [] as string[],
    fromDate: `${year}-${String(month).padStart(2, '0')}-01`,
    toDate: `${year}-${String(month).padStart(2, '0')}-${lastDay}`,
    type: 'MORNING',
    shiftDefinitionId: '',
    weekdays: [] as number[],
    overwrite: false,
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });
  const { data: definitions } = useQuery({
    queryKey: ['shift-definitions'],
    queryFn: () => api.get<any[]>('/shift-definitions'),
  });

  const generate = useMutation({
    mutationFn: () =>
      api.post<any>('/shift-schedules/bulk', {
        ...form,
        shiftDefinitionId: form.type === 'OFF' ? undefined : form.shiftDefinitionId || undefined,
        weekdays: form.weekdays.length ? form.weekdays : undefined,
      }),
    onSuccess: (result) => {
      toast.success(`${result.written} day(s) written, ${result.skipped} skipped.`);
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      navigate(BACK);
    },
    onError: (e: any) => toast.error(refusalText(e, t)),
  });

  const WEEKDAY_LABELS =
    locale === 'ar'
      ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <FormPage
      title={t('shifts.generate')}
      backTo={BACK}
      backLabel={t('nav.shifts')}
      width="lg"
      submitLabel={t('common.apply')}
      submitting={generate.isPending}
      disabled={form.userIds.length === 0 || (form.type !== 'OFF' && !form.shiftDefinitionId)}
      onSubmit={() => generate.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('nav.employees')} required>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border hairline p-2">
            {(users ?? []).map((u) => (
              <Checkbox
                key={u.id}
                checked={form.userIds.includes(u.id)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    userIds: e.target.checked
                      ? [...form.userIds, u.id]
                      : form.userIds.filter((x) => x !== u.id),
                  })
                }
                label={localized(u)}
                className="block"
              />
            ))}
          </div>
        </Field>

        <FormGrid>
          <Field label={t('common.from')} required>
            <Input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} />
          </Field>
          <Field label={t('common.to')} required>
            <Input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} />
          </Field>
          <Field label={t('common.type')}>
            <Select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value, shiftDefinitionId: '' })}
            >
              {['MORNING', 'NIGHT', 'CUSTOM', 'OFF'].map((x) => (
                <option key={x} value={x}>{enumLabel(x, locale)}</option>
              ))}
            </Select>
          </Field>
          {form.type !== 'OFF' && (
            <Field label={t('shifts.definitions')} required>
              <Select
                value={form.shiftDefinitionId}
                onChange={(e) => setForm({ ...form, shiftDefinitionId: e.target.value })}
              >
                <option value="">—</option>
                {(definitions ?? [])
                  .filter((d) => d.type === form.type)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({shiftHoursLabel(d, t, locale)})
                    </option>
                  ))}
              </Select>
            </Field>
          )}
        </FormGrid>

        <Field label={t('shifts.weekdays')} hint={t('shifts.weekdaysHint')}>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, index) => (
              <button
                key={index}
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    weekdays: form.weekdays.includes(index)
                      ? form.weekdays.filter((d) => d !== index)
                      : [...form.weekdays, index],
                  })
                }
                className={clsx(
                  'rounded-pill px-3 py-1 text-caption font-medium ring-1 ring-inset',
                  form.weekdays.includes(index)
                    ? 'bg-primary/15 text-primary-600 dark:text-primary-400 ring-primary/40'
                    : 'text-[var(--text-secondary)] ring-[var(--line)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        <Checkbox
          checked={form.overwrite}
          onChange={(e) => setForm({ ...form, overwrite: e.target.checked })}
          label={t('shifts.overwrite')}
        />
      </div>
    </FormPage>
  );
}

/** Route: `/shifts/copy`. */
export function CopyRosterFormPage() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    userIds: [] as string[],
    sourceFrom: '',
    sourceTo: '',
    targetFrom: isoDate(),
    overwrite: false,
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });

  const copy = useMutation({
    mutationFn: () => api.post<any>('/shift-schedules/copy', form),
    onSuccess: (result) => {
      toast.success(`${result.written} day(s) copied, ${result.skipped} skipped.`);
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      navigate(BACK);
    },
    onError: (e: any) => toast.error(refusalText(e, t)),
  });

  return (
    <FormPage
      title={t('shifts.copy')}
      backTo={BACK}
      backLabel={t('nav.shifts')}
      submitLabel={t('common.apply')}
      submitting={copy.isPending}
      disabled={!form.userIds.length || !form.sourceFrom || !form.sourceTo || !form.targetFrom}
      onSubmit={() => copy.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('nav.employees')} required>
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border hairline p-2">
            {(users ?? []).map((u) => (
              <Checkbox
                key={u.id}
                checked={form.userIds.includes(u.id)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    userIds: e.target.checked ? [...form.userIds, u.id] : form.userIds.filter((x) => x !== u.id),
                  })
                }
                label={localized(u)}
                className="block"
              />
            ))}
          </div>
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={`${t('common.from')} (source)`} required>
            <Input
              type="date"
              value={form.sourceFrom}
              onChange={(e) => setForm({ ...form, sourceFrom: e.target.value })}
            />
          </Field>
          <Field label={`${t('common.to')} (source)`} required>
            <Input type="date" value={form.sourceTo} onChange={(e) => setForm({ ...form, sourceTo: e.target.value })} />
          </Field>
          <Field label={`${t('common.from')} (target)`} required>
            <Input
              type="date"
              value={form.targetFrom}
              onChange={(e) => setForm({ ...form, targetFrom: e.target.value })}
            />
          </Field>
        </div>
        <Checkbox
          checked={form.overwrite}
          onChange={(e) => setForm({ ...form, overwrite: e.target.checked })}
          label={t('shifts.overwrite')}
        />
      </div>
    </FormPage>
  );
}

/**
 * A definition's hours — "08:00–16:00" — and, when new hours are booked for a
 * later day, those too: "08:00–16:00 · 09:00–17:00 from 1 Oct 2026".
 */
export function shiftHoursLabel(
  definition: any,
  t: ReturnType<typeof useI18n>['t'],
  locale: Locale,
): string {
  const hours = `${definition.startTime}–${definition.endTime}`;
  if (!definition.nextFrom) return hours;
  const next = `${definition.nextStartTime}–${definition.nextEndTime}`;
  return `${hours} · ${t('shifts.nextHours', { hours: next, date: formatDate(definition.nextFrom, locale) })}`;
}

type ApplyFrom = 'TODAY' | 'NEXT_MONTH';

/** The early-end rule's limits, as the API holds them: minutes 0–720, a penalty above 0 and at most 30 days. */
const EARLY_END_MAX_MINUTES = 720;
const PENALTY_DAYS = /^\d{1,2}(\.\d{1,2})?$/;

/**
 * Routes: `/shifts/definitions/new` and `/shifts/definitions/:id/edit`.
 *
 * Changing the hours of a definition asks when they start — today, or the 1st
 * of next month. Saved days from that date on move to them; the API leaves the
 * days before it, and days given hours of their own, as they are.
 *
 * Ending the shift early (operator, 2026-11-03): how many minutes before its end
 * a day's shift may end, and what ending it earlier costs — even on a day whose
 * hours were worked. It applies at once, to shifts started after the save, so it
 * never asks when.
 */
export function ShiftDefinitionFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['shift-definition', id],
    queryFn: () => api.get<any>(`/shift-definitions/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    name: '',
    nameAr: '',
    type: 'CUSTOM',
    startTime: '09:00',
    endTime: '17:00',
    standardHours: 8,
    isActive: true,
  });
  const [applyFrom, setApplyFrom] = useState<ApplyFrom | ''>('');
  const [earlyEnd, setEarlyEnd] = useState({ on: false, minutes: '15', days: '0.25' });

  const saved = existing.data;
  // The hours it runs from here on: the booked ones, when a change is booked.
  const savedHours = saved
    ? saved.nextFrom
      ? { startTime: saved.nextStartTime, endTime: saved.nextEndTime, standardHours: Number(saved.nextStandardHours) }
      : { startTime: saved.startTime, endTime: saved.endTime, standardHours: Number(saved.standardHours) }
    : null;
  const [loaded, setLoaded] = useState(false);
  if (editing && saved && savedHours && !loaded) {
    setLoaded(true);
    setForm({ name: saved.name, nameAr: saved.nameAr ?? '', type: saved.type, ...savedHours, isActive: saved.isActive });
    if (saved.earlyEndMinutes != null) {
      setEarlyEnd({ on: true, minutes: String(saved.earlyEndMinutes), days: String(Number(saved.earlyEndPenaltyDays)) });
    }
  }

  const hoursChanged =
    !!savedHours &&
    (form.startTime !== savedHours.startTime ||
      form.endTime !== savedHours.endTime ||
      Number(form.standardHours) !== savedHours.standardHours);

  const earlyMinutes = Number(earlyEnd.minutes);
  const earlyEndValid =
    !earlyEnd.on ||
    (earlyEnd.minutes.trim() !== '' &&
      Number.isInteger(earlyMinutes) &&
      earlyMinutes >= 0 &&
      earlyMinutes <= EARLY_END_MAX_MINUTES &&
      PENALTY_DAYS.test(earlyEnd.days.trim()) &&
      Number(earlyEnd.days) > 0 &&
      Number(earlyEnd.days) <= 30);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        name: form.name.trim(),
        nameAr: form.nameAr.trim() || null,
        ...(hoursChanged ? { applyFrom } : {}),
        // Off is null: the API clears the rule.
        earlyEndMinutes: earlyEnd.on ? earlyMinutes : null,
        earlyEndPenaltyDays: earlyEnd.on ? Number(earlyEnd.days) : null,
      };
      return editing ? api.patch<any>(`/shift-definitions/${id}`, body) : api.post<any>('/shift-definitions', body);
    },
    onSuccess: (result) => {
      const applied = result?.applied;
      toast.success(
        applied
          ? [
              t('shifts.hoursApplied', { date: formatDate(applied.from, locale), moved: applied.moved }),
              applied.kept ? t('shifts.hoursKept', { kept: applied.kept }) : '',
            ]
              .filter(Boolean)
              .join(' ')
          : t('shifts.definitionSaved'),
      );
      void queryClient.invalidateQueries({ queryKey: ['shift-definitions'] });
      void queryClient.invalidateQueries({ queryKey: ['shift-definition'] });
      if (applied) {
        void queryClient.invalidateQueries({ queryKey: ['roster'] });
        void queryClient.invalidateQueries({ queryKey: ['my-schedule'] });
      }
      navigate(DEFINITIONS_BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const applyOptions: Array<{ id: ApplyFrom; label: string; hint: string }> = saved
    ? [
        {
          id: 'TODAY',
          label: t('shifts.applyToday', { date: formatDate(saved.applyDates.TODAY, locale) }),
          hint: t('shifts.applyTodayHint'),
        },
        {
          id: 'NEXT_MONTH',
          label: t('shifts.applyNextMonth', { date: formatDate(saved.applyDates.NEXT_MONTH, locale) }),
          hint: t('shifts.applyNextMonthHint', { date: formatDate(saved.applyDates.NEXT_MONTH, locale) }),
        },
      ]
    : [];

  return (
    <FormPage
      title={editing ? t('shifts.editDefinition') : t('shifts.newDefinition')}
      subtitle={
        saved?.nextFrom
          ? t('shifts.bookedNote', {
              current: `${saved.startTime}–${saved.endTime}`,
              date: formatDate(saved.nextFrom, locale),
            })
          : undefined
      }
      backTo={DEFINITIONS_BACK}
      backLabel={t('nav.shifts')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.name.trim() || (hoursChanged && !applyFrom) || !earlyEndValid}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.type')} hint={editing ? t('shifts.typeFixed') : undefined}>
          <Select value={form.type} disabled={editing} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {['MORNING', 'NIGHT', 'CUSTOM'].map((x) => (
              <option key={x} value={x}>{enumLabel(x, locale)}</option>
            ))}
          </Select>
        </Field>
        {/* The hours a day on this shift owes (2026-10-21): the daily-hours rule judges each day against them. */}
        <Field label={t('shifts.standardHours')} hint={t('shifts.standardHoursHint')}>
          <Input
            type="number"
            step="0.5"
            value={form.standardHours}
            onChange={(e) => setForm({ ...form, standardHours: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('shifts.startTime')} required>
          <Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
        </Field>
        <Field label={t('shifts.endTime')} required hint={t('shifts.midnightHint')}>
          <Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
        </Field>
        {editing && (
          <Field label={t('common.status')} hint={t('settings.offeredHint')}>
            <Checkbox
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              label={form.isActive ? t('settings.offered') : t('settings.switchedOff')}
            />
          </Field>
        )}

        {hoursChanged && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('shifts.applyFrom')}
              <span className="text-primary dark:text-primary-400">*</span>
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {applyOptions.map((option) => (
                <label
                  key={option.id}
                  className={clsx(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-fast',
                    applyFrom === option.id ? 'border-primary/60 bg-primary/8' : 'hairline hover:border-primary/30',
                  )}
                >
                  <input
                    type="radio"
                    name="applyFrom"
                    value={option.id}
                    checked={applyFrom === option.id}
                    onChange={() => setApplyFrom(option.id)}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                  />
                  <span className="min-w-0">
                    <span className="block text-body-sm font-medium text-[var(--text-primary)]">{option.label}</span>
                    <span className="mt-0.5 block text-caption text-muted">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-caption text-faint">{t('shifts.applyKeepsNote')}</p>
          </fieldset>
        )}

        {/* Ending the shift early (2026-11-03): allowed so many minutes before its end; earlier costs a penalty. */}
        <div className="space-y-3 border-t hairline pt-4 sm:col-span-2">
          <p className="text-overline uppercase text-[var(--text-tertiary)]">{t('shifts.earlyEnd')}</p>
          <Checkbox
            checked={earlyEnd.on}
            onChange={(e) => setEarlyEnd({ ...earlyEnd, on: e.target.checked })}
            label={t('shifts.earlyEndOn')}
          />
          {earlyEnd.on && (
            <>
              <FormGrid>
                <Field label={t('shifts.earlyEndMinutes')} required hint={t('shifts.earlyEndMinutesHint')}>
                  <Input
                    type="number"
                    min={0}
                    max={EARLY_END_MAX_MINUTES}
                    step={1}
                    value={earlyEnd.minutes}
                    onChange={(e) => setEarlyEnd({ ...earlyEnd, minutes: e.target.value })}
                  />
                </Field>
                <Field label={t('shifts.earlyEndPenalty')} required hint={t('shifts.earlyEndPenaltyHint')}>
                  <Input
                    type="number"
                    min={0.25}
                    max={30}
                    step={0.25}
                    value={earlyEnd.days}
                    onChange={(e) => setEarlyEnd({ ...earlyEnd, days: e.target.value })}
                  />
                </Field>
              </FormGrid>
              <p className="text-caption text-muted">{t('shifts.earlyEndNote')}</p>
            </>
          )}
        </div>
      </FormGrid>
    </FormPage>
  );
}

/**
 * Route: `/shifts/definitions/:id/remove`, confirmed on a page as every action
 * here is. Removing hides a definition — from the list and every roster choice —
 * while the saved days that run it keep it, with their hours (2026-11-02).
 */
export function ShiftDefinitionRemovePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const definition = useQuery({
    queryKey: ['shift-definition', id],
    queryFn: () => api.get<any>(`/shift-definitions/${id}`),
  });

  const remove = useMutation({
    mutationFn: () => api.del<any>(`/shift-definitions/${id}`),
    onSuccess: () => {
      toast.success(t('shifts.definitionRemoved'));
      // Not ['shift-definition', id]: this page still watches it, and it is gone.
      void queryClient.invalidateQueries({ queryKey: ['shift-definitions'] });
      navigate(DEFINITIONS_BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const upcomingDays = definition.data?.upcomingDays ?? 0;

  return (
    <FormPage
      title={t('shifts.removeDefinition')}
      subtitle={
        definition.data ? `${localized(definition.data)} — ${shiftHoursLabel(definition.data, t, locale)}` : undefined
      }
      backTo={DEFINITIONS_BACK}
      backLabel={t('nav.shifts')}
      submitLabel={t('common.remove')}
      destructive
      submitting={remove.isPending}
      loading={definition.isLoading}
      error={definition.error}
      onRetry={() => void definition.refetch()}
      onSubmit={() => remove.mutate()}
    >
      <div className="space-y-3">
        <p className="text-body text-[var(--text-secondary)]">{t('shifts.removeDefinitionNote')}</p>
        {upcomingDays > 0 && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('shifts.removeDefinitionUpcoming', { days: upcomingDays })}
          </p>
        )}
      </div>
    </FormPage>
  );
}

/** The toast after a holiday change — with what was counted again, when anything was. */
function holidayToast(done: string, applied: any, t: ReturnType<typeof useI18n>['t'], locale: Locale): string {
  if (!applied || (!applied.leaveRequests && !applied.workLogs)) return done;
  const again = t('shifts.holidayApplied', {
    date: formatDate(applied.from, locale),
    leave: applied.leaveRequests,
    logs: applied.workLogs,
  });
  return `${done} ${again}`;
}

/** A holiday change shows on the roster, in leave and on the attendance board. */
function refreshHolidayViews(queryClient: ReturnType<typeof useQueryClient>): void {
  for (const key of ['holidays', 'roster', 'leaves', 'attendance']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Routes: `/shifts/holidays/new` and `/shifts/holidays/:id/edit`. A change
 * applies at once, from today — the API counts leave and work logs again for
 * the dates it touches, and leaves everything before today as it was.
 */
export function HolidayFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['holiday', id],
    queryFn: () => api.get<any>(`/holidays/${id}`),
    enabled: editing,
  });

  // A holiday is a name and a date: its pay multiplier went on 2026-10-29 — nothing read it.
  const [form, setForm] = useState({ name: '', nameAr: '', date: isoDate() });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    const holiday = existing.data;
    setLoaded(true);
    setForm({
      name: holiday.name,
      nameAr: holiday.nameAr ?? '',
      date: holiday.date.slice(0, 10),
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        nameAr: form.nameAr.trim() || null,
        date: form.date,
      };
      return editing ? api.patch<any>(`/holidays/${id}`, body) : api.post<any>('/holidays', body);
    },
    onSuccess: (result) => {
      toast.success(holidayToast(t('shifts.holidaySaved'), result?.applied, t, locale));
      refreshHolidayViews(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['holiday'] });
      navigate(HOLIDAYS_BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={editing ? t('shifts.editHoliday') : t('shifts.newHoliday')}
      subtitle={t('shifts.holidayAppliesNote')}
      backTo={HOLIDAYS_BACK}
      backLabel={t('nav.shifts')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.name.trim() || !form.date}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.date')} required>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/shifts/holidays/:id/delete`. Deleting is confirmed on a page, as every action here is. */
export function HolidayDeletePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const holiday = useQuery({
    queryKey: ['holiday', id],
    queryFn: () => api.get<any>(`/holidays/${id}`),
  });

  const remove = useMutation({
    mutationFn: () => api.del<any>(`/holidays/${id}`),
    onSuccess: (result) => {
      toast.success(holidayToast(t('shifts.holidayDeleted'), result?.applied, t, locale));
      // Not ['holiday', id]: this page still watches it, and it is gone.
      refreshHolidayViews(queryClient);
      navigate(HOLIDAYS_BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('shifts.deleteHoliday')}
      subtitle={holiday.data ? `${localized(holiday.data)} — ${formatDate(holiday.data.date, locale)}` : undefined}
      backTo={HOLIDAYS_BACK}
      backLabel={t('nav.shifts')}
      submitLabel={t('common.delete')}
      destructive
      submitting={remove.isPending}
      loading={holiday.isLoading}
      error={holiday.error}
      onRetry={() => void holiday.refetch()}
      onSubmit={() => remove.mutate()}
    >
      <p className="text-body text-[var(--text-secondary)]">{t('shifts.deleteHolidayNote')}</p>
    </FormPage>
  );
}
